<template>
  <v-row>
    <v-col cols="12">
      <v-card class="prod-card" flat>
        <!-- HEADER BAND -->
        <div class="prod-header pl-base-bg">
          <div class="prod-header-icon">
            <v-icon color="#fff" size="22">mdi-package-variant</v-icon>
          </div>
          <div>
            <div class="prod-title">Edit Product</div>
            <div class="prod-subtitle">{{ form.productName || "Update product details" }}</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="prod-section-label">Product Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.productName"
                label="Product Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-cube-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.hsn"
                label="HSN Code"
                outlined
                dense
                prepend-inner-icon="mdi-barcode"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.rate"
                label="Rate (₹)"
                type="number"
                outlined
                dense
                prepend-inner-icon="mdi-currency-inr"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.costPrice"
                label="Cost Price (₹) — optional"
                type="number"
                outlined
                dense
                hint="Used only for an estimated inventory value on the Balance Sheet"
                persistent-hint
                prepend-inner-icon="mdi-tag-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-select
                v-model="form.uqc"
                :items="uqcCodes"
                item-text="label"
                item-value="code"
                label="Unit (UQC)"
                outlined
                dense
                prepend-inner-icon="mdi-ruler"
              >
                <template #selection="{ item }">{{ item.code }}</template>
                <template #item="{ item }">{{ item.code }} — {{ item.label }}</template>
              </v-select>
            </v-col>
          </v-row>

          <div class="prod-section-label mt-4">Stock</div>
          <v-row dense>
            <v-col cols="12" sm="4" class="d-flex align-start prod-track-stock-col">
              <v-switch
                v-model="form.trackStock"
                label="Track Stock"
                dense
                inset
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.stockQuantity"
                label="Stock Quantity"
                type="number"
                outlined
                dense
                :disabled="!form.trackStock"
                prepend-inner-icon="mdi-cube-scan"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.lowStockThreshold"
                label="Low Stock Alert Threshold"
                type="number"
                outlined
                dense
                :disabled="!form.trackStock"
                prepend-inner-icon="mdi-alert-outline"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block color="error" outlined @click="confirmDelete = true">
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn
                block
                class="prod-save-btn"
                :loading="loading"
                @click="submit"
              >
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this product?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";
import { UQC_CODES } from "@/utils/uqc-codes";

export default {
  name: "EditProduct",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      uqcCodes: UQC_CODES,

      productId: null,

      originalForm: null,

      form: {
        productName: "",
        hsn: "",
        rate: null,
        costPrice: null,
        trackStock: false,
        stockQuantity: 0,
        lowStockThreshold: 0,
        uqc: "NOS",
      },
      confirmDelete: false,

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  created() {
    this.productId = this.$route.params.id;
    this.loadProduct();
  },

  computed: {
    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },
  },

  methods: {
    ...mapActions("products", ["getProduct", "updateProduct", "deleteProduct"]),

    /* ================= LOAD PRODUCT ================= */
    async loadProduct() {
      try {
        const data = await this.getProduct(this.productId);

        this.form = {
          productName: data.productName,
          hsn: data.hsn,
          rate: data.rate,
          costPrice: data.costPrice,
          trackStock: data.trackStock || false,
          stockQuantity: data.stockQuantity || 0,
          lowStockThreshold: data.lowStockThreshold || 0,
          uqc: data.uqc || "NOS",
        };

        this.originalForm = { ...this.form };
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to load product",
        );
      }
    },

    /* ================= UPDATE ================= */
    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        await this.updateProduct({
          id: this.productId,
          payload: this.form,
        });

        this.$toast?.success("Product updated successfully");

        this.$router.push("/products");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update product",
        );
      } finally {
        this.loading = false;
      }
    },

    /* ================= RESET ================= */
    reset() {
      if (this.originalForm) {
        this.form = { ...this.originalForm };
      }
    },

    async delete() {
      try {
        await this.deleteProduct(this.productId);

        this.$toast?.success("Product deleted successfully");

        this.$router.push("/products");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete product",
        );
      } finally {
        this.confirmDelete = false;
      }
    },
  },
};
</script>

<style scoped>
.prod-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.prod-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.prod-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.prod-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.prod-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.prod-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

/* Column uses align-start (not align-center, which would center against
   the row's tallest sibling — the outlined text fields, whose box reserves
   space above the input for their border-notch label) so the switch anchors
   to the top, then nudges down by a small, precisely-tuned offset to land
   its toggle on the same line as the text fields' input text. */
.prod-track-stock-col >>> .v-input--switch {
  margin-top: 8px;
}

.prod-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
